// Cross-domain landers list (MIS-810 / Task 29; setup badge FF-23 / MIS-1006;
// global New Lander FF-25 / MIS-1008).
//
// Lists every lander the active tenant owns (the `/api/landers` repo is
// tenant-scoped) in the shared DataTable: slug, headline, template, domain,
// setup (routing+binding) badge, last updated, and per-row actions (Edit
// design — the Puck builder deep link when the lander references a template
// (FF-26 / MIS-1009; the param is the TEMPLATE id) — Edit, Delete). The
// toolbar's "New Lander" button (FF-25) opens NewLanderDialog WITHOUT a
// domainId, so the dialog shows its own domain picker and defaults the
// random-slug toggle ON. Client-side
// filters narrow by domain, template, and setup
// readiness (All/Ready/Incomplete pills). Domains are fetched alongside so the
// table can show the hostname (the lander row only carries `domainId`) and to
// populate the domain filter. The setup badge + readiness filter derive from
// ONE aggregate `GET /api/setup-status` query (`useSetupStatuses()` — shared
// with the domains list via the same key; never per-row fetches): each
// lander's `numberRouting.perLander` mode + its domain's `fbBinding` check.
// The list is always fetched fresh from the server (React Query) and
// refetches when the active tenant changes (useApi re-injects X-Tenant-Id).
//
// Delete uses a confirm Dialog → `DELETE /api/landers/:id` (a HARD delete — the
// schema has no soft-delete column) → invalidate. A 403 (viewer-only on the
// lander's domain) surfaces as a toast; the API is the source of truth for RBAC.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { formatTimestamp } from '@/components/domains/format';
import {
  LanderSetupBadge,
  type ReadinessFilter,
  ReadinessFilterPills,
  resolveLanderSetupBadge,
  useSetupStatuses,
} from '@/components/domains/SetupStatusBadge';
import { BulkCreateDialog } from '@/components/landers/BulkCreateDialog';
import { CloneLanderDialog } from '@/components/landers/CloneLanderDialog';
import { NewLanderDialog } from '@/components/landers/NewLanderDialog';
import { type Lander, TEMPLATE_LABELS, TEMPLATES } from '@/components/landers/types';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataTable } from '@/components/ui/DataTable';
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Label } from '@/components/ui/Label';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/components/ui/toast';
import { useApi } from '@/lib/api';
import { useTenantContext } from '@/lib/auth';

export const Route = createFileRoute('/_authenticated/landers/')({
  component: LandersListPage,
});

function LandersListPage(): React.ReactElement {
  const api = useApi();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { activeTenantId } = useTenantContext();

  const [domainFilter, setDomainFilter] = useState<string>('all');
  const [templateFilter, setTemplateFilter] = useState<string>('all');
  const [readinessFilter, setReadinessFilter] = useState<ReadinessFilter>('all');
  const [pendingDelete, setPendingDelete] = useState<Lander | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  // FF-24: per-row clone target + the toolbar bulk-create dialog.
  const [pendingClone, setPendingClone] = useState<Lander | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const {
    data: landers,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['landers', activeTenantId],
    queryFn: async () => {
      const res = await api.api.landers.$get();
      if (!res.ok) throw new Error('Failed to load landers');
      const data = await res.json();
      return data.landers;
    },
  });

  // Domains power the host column + the domain filter. Keyed by tenant too.
  const { data: domains } = useQuery({
    queryKey: ['domains', activeTenantId],
    queryFn: async () => {
      const res = await api.api.domains.$get();
      if (!res.ok) throw new Error('Failed to load domains');
      const data = await res.json();
      return data.domains;
    },
  });

  const hostById = useMemo(() => {
    const map = new Map<number, string>();
    for (const d of domains ?? []) map.set(d.id, d.host);
    return map;
  }, [domains]);

  // ONE aggregate setup-status fetch for the whole table (FF-23): the
  // routing+binding badge and the readiness pills both read from it.
  const { data: setupStatuses } = useSetupStatuses();

  const statusByDomain = useMemo(() => {
    const map = new Map<number, NonNullable<typeof setupStatuses>[number]>();
    for (const s of setupStatuses ?? []) map.set(s.domainId, s);
    return map;
  }, [setupStatuses]);

  const deleteMutation = useMutation({
    mutationFn: async (landerId: number) => {
      const res = await api.api.landers[':id'].$delete({ param: { id: String(landerId) } });
      if (!res.ok) {
        if (res.status === 403) throw new Error("You don't have edit access to this lander.");
        throw new Error(`Delete failed (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['landers'] });
      toast({ title: 'Lander deleted', variant: 'success' });
      setPendingDelete(null);
    },
    onError: (err: Error) => {
      toast({ title: 'Could not delete lander', description: err.message, variant: 'destructive' });
    },
  });

  const filtered = useMemo(() => {
    return (landers ?? []).filter((lander) => {
      if (domainFilter !== 'all' && String(lander.domainId) !== domainFilter) return false;
      if (templateFilter !== 'all' && lander.template !== templateFilter) return false;
      if (readinessFilter !== 'all') {
        // Ready ⇔ green routing+binding; unknown/not-loaded counts as incomplete.
        const ready =
          resolveLanderSetupBadge(statusByDomain.get(lander.domainId), lander.id)?.ready === true;
        if (readinessFilter === 'ready' ? !ready : ready) return false;
      }
      return true;
    });
  }, [landers, domainFilter, templateFilter, readinessFilter, statusByDomain]);

  const columns = useMemo<ColumnDef<Lander, unknown>[]>(
    () => [
      {
        accessorKey: 'slug',
        header: 'Slug',
        cell: ({ row }) => (
          <Link
            to="/landers/$landerId/edit"
            params={{ landerId: String(row.original.id) }}
            className="font-mono font-medium text-gray-900 hover:underline"
          >
            {row.original.slug}
          </Link>
        ),
      },
      {
        accessorKey: 'headline',
        header: 'Headline',
        cell: ({ row }) => row.original.headline ?? <span className="text-gray-400">—</span>,
      },
      {
        id: 'template',
        header: 'Template',
        cell: ({ row }) => <Badge variant="gray">{TEMPLATE_LABELS[row.original.template]}</Badge>,
      },
      {
        id: 'domain',
        header: 'Domain',
        cell: ({ row }) => {
          const host = hostById.get(row.original.domainId);
          return host ? (
            <Link
              to="/domains/$domainId/landers"
              params={{ domainId: String(row.original.domainId) }}
              className="text-gray-600 hover:underline"
            >
              {host}
            </Link>
          ) : (
            <span className="text-gray-400">#{row.original.domainId}</span>
          );
        },
      },
      {
        id: 'setup',
        header: 'Setup',
        cell: ({ row }) => (
          <LanderSetupBadge
            status={statusByDomain.get(row.original.domainId)}
            landerId={row.original.id}
          />
        ),
      },
      {
        id: 'updated',
        header: 'Last updated',
        cell: ({ row }) => {
          const ts = row.original.updatedAt ?? row.original.createdAt;
          return <span className="text-gray-500">{ts ? formatTimestamp(ts) : '—'}</span>;
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-2">
            {/* FF-26 (MIS-1009) — "Edit design" deep link to the Puck builder.
                The builder param is the TEMPLATE id (live reference), never the
                lander id. templateId null + materialized puckJson = per-lander
                snapshot: no instance builder in scope, hint only. */}
            {row.original.templateId != null ? (
              <Link to="/templates/$id/builder" params={{ id: String(row.original.templateId) }}>
                <Button variant="ghost" size="sm">
                  Edit design
                </Button>
              </Link>
            ) : row.original.puckJson != null ? (
              <span
                className="text-xs text-gray-400"
                title="per-lander snapshot — edit via template"
              >
                snapshot
              </span>
            ) : null}
            <Link to="/landers/$landerId/edit" params={{ landerId: String(row.original.id) }}>
              <Button variant="ghost" size="sm">
                Edit
              </Button>
            </Link>
            {/* FF-24: clone this lander (same or another domain, 1–20 copies). */}
            <Button variant="ghost" size="sm" onClick={() => setPendingClone(row.original)}>
              Clone
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600 hover:bg-red-50"
              onClick={() => setPendingDelete(row.original)}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [hostById, statusByDomain],
  );

  return (
    <div className="space-y-6 p-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Landers</h1>
          <p className="text-sm text-gray-500">All landing pages across this workspace.</p>
        </div>
        <div className="flex items-center gap-2">
          {/* FF-24: template-bound landers across many domains in one go. */}
          <Button variant="outline" onClick={() => setBulkOpen(true)}>
            Bulk Create
          </Button>
          {/* FF-25: global create — no domainId, the dialog shows a domain picker. */}
          <Button onClick={() => setNewOpen(true)}>New Lander</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="filter-domain">Domain</Label>
          <Select
            id="filter-domain"
            value={domainFilter}
            onChange={(e) => setDomainFilter(e.target.value)}
            className="w-56"
          >
            <option value="all">All domains</option>
            {(domains ?? []).map((d) => (
              <option key={d.id} value={String(d.id)}>
                {d.host}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="filter-template">Template</Label>
          <Select
            id="filter-template"
            value={templateFilter}
            onChange={(e) => setTemplateFilter(e.target.value)}
            className="w-40"
          >
            <option value="all">All templates</option>
            {TEMPLATES.map((t) => (
              <option key={t} value={t}>
                {TEMPLATE_LABELS[t]}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1.5">
          <span className="block text-sm font-medium text-[var(--muted)]">Setup</span>
          <ReadinessFilterPills value={readinessFilter} onChange={setReadinessFilter} />
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading landers…</p>
      ) : isError ? (
        <p className="text-sm text-red-600">Failed to load landers. Try again.</p>
      ) : (
        <DataTable
          columns={columns}
          data={filtered}
          emptyMessage="No landers yet. Click New Lander to create one."
        />
      )}

      <NewLanderDialog open={newOpen} onOpenChange={setNewOpen} />

      <BulkCreateDialog open={bulkOpen} onOpenChange={setBulkOpen} />

      {/* Mounted per-source so the dialog's queries/state key off the row. */}
      {pendingClone !== null && (
        <CloneLanderDialog
          landerId={pendingClone.id}
          sourceDomainId={pendingClone.domainId}
          open
          onOpenChange={(isOpen) => !isOpen && setPendingClone(null)}
        />
      )}

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        ariaLabel="Confirm delete lander"
      >
        <DialogHeader>
          <DialogTitle>Delete lander</DialogTitle>
          <DialogDescription>
            This permanently removes the lander. It can't be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm text-gray-700">
            Delete <span className="font-mono font-medium">{pendingDelete?.slug}</span>?
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => setPendingDelete(null)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={deleteMutation.isPending}
            onClick={() => pendingDelete && deleteMutation.mutate(pendingDelete.id)}
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
